import { orderKey, flattenWalletOrders } from '@/helpers/orders'
import { Order, OrderStatus, Wallet } from '@/types/booking'
import { useMemo } from 'react'
import { create } from 'zustand'

// Define the store's state type
type WalletState = {
  wallet: Wallet | undefined
  // Flat list of every order across all bookings, derived whenever the wallet
  // is set. Kept in the store only so imperative readers (getState()) can reach
  // it; upcoming/past are derived on read via the selectors below.
  walletOrders: Order[] | undefined
  ticketVideoOnMap: Record<string, boolean>
  // Keyed by orderKey(uid, instance_uid) — an order can span two instances.
  optimisticStatuses: Record<string, OrderStatus>
  walletPending: boolean
  syncBookingInProgress: boolean
  walletTab: 'tickets' | 'memories'
  setWalletTab: (tab: 'tickets' | 'memories') => void
  setWallet: (wallet: Wallet | undefined) => void
  removeUpBooking: () => void
  setTicketVideoOn: (ticketId: string, value: boolean) => void
  setOptimisticStatus: (
    orderUid: string,
    instanceUid: string | undefined,
    status: OrderStatus,
  ) => void
  clearOptimisticStatus: (
    orderUid: string,
    instanceUid: string | undefined,
  ) => void
  clearAllOptimisticStatuses: () => void
  setWalletPending: (pending: boolean) => void
  setSyncBookingInProgress: (inProgress: boolean) => void
}

// Create the store
export const useTicketsStore = create<WalletState>((set) => ({
  wallet: undefined,
  walletTab: 'tickets',
  walletOrders: undefined,
  ticketVideoOnMap: {},
  optimisticStatuses: {},
  walletPending: false,
  syncBookingInProgress: false,
  setWalletTab: (walletTab) => set({ walletTab }),
  // Setting the wallet re-derives the flat order list in lockstep, so the two
  // can never drift.
  setWallet: (wallet) =>
    set({
      wallet,
      walletOrders: wallet
        ? flattenWalletOrders(wallet.bookings).allOrders
        : undefined,
    }),
  removeUpBooking: () => set({ wallet: undefined, walletOrders: undefined }),
  setTicketVideoOn: (ticketId, value) =>
    set((state) => ({
      ticketVideoOnMap: { ...state.ticketVideoOnMap, [ticketId]: value },
    })),
  setOptimisticStatus: (orderUid, instanceUid, status) =>
    set((state) => ({
      optimisticStatuses: {
        ...state.optimisticStatuses,
        [orderKey(orderUid, instanceUid)]: status,
      },
    })),
  clearOptimisticStatus: (orderUid, instanceUid) =>
    set((state) => {
      const { [orderKey(orderUid, instanceUid)]: _, ...rest } =
        state.optimisticStatuses
      return { optimisticStatuses: rest }
    }),
  clearAllOptimisticStatuses: () => set({ optimisticStatuses: {} }),
  setWalletPending: (walletPending) => set({ walletPending }),
  setSyncBookingInProgress: (syncBookingInProgress) =>
    set({ syncBookingInProgress }),
}))

// Derived selector — the upcoming list is a pure function of the wallet, so we
// compute it on read instead of storing (and hand-syncing) a separate array.
// Memoised on the wallet: flattenWalletOrders clones orders that carry no
// instance_uid, so recomputing per render would hand out new identities and
// loop any effect keyed on an order.
export const useUpcomingOrders = (): Order[] | undefined => {
  const wallet = useTicketsStore((s) => s.wallet)
  return useMemo(
    () =>
      wallet ? flattenWalletOrders(wallet.bookings).upcomingOrders : undefined,
    [wallet],
  )
}
